import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

function ServerError() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center px-4">
      <div className="max-w-4xl mx-auto text-center">
        {/* Animated 500 */}
        <div className="mb-8">
          <h1 className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-600 animate-pulse">
            500
          </h1>
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-32 border-4 border-red-500 border-opacity-30 rounded-full animate-spin"></div>
              <div className="absolute w-24 h-24 border-4 border-orange-500 border-opacity-30 rounded-full animate-spin" style={{animationDirection: 'reverse', animationDuration: '2s'}}></div>
              <div className="absolute w-16 h-16 border-4 border-yellow-500 border-opacity-30 rounded-full animate-spin" style={{animationDuration: '1s'}}></div>
            </div>
          </div>
        </div>

        {/* Main Message */}
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ops! Nosso <span className="text-red-500">Servidor</span> Está em Manutenção
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Parece que nossos robôs digitais estão tendo um dia difícil. Nossa equipe de engenheiros já foi acionada 
            e está trabalhando para resolver este problema o mais rápido possível!
          </p>
        </div>

        {/* Fun Illustration */}
        <div className="mb-12">
          <div className="relative inline-block">
            <div className="text-8xl mb-4 animate-bounce">🤖</div>
            <div className="absolute -top-4 -right-4 text-4xl animate-ping">⚡</div>
            <div className="absolute -bottom-2 -left-4 text-3xl animate-pulse">🔧</div>
            <div className="absolute top-8 -left-8 text-2xl animate-bounce" style={{animationDelay: '0.5s'}}>⚙️</div>
            <div className="absolute bottom-4 -right-6 text-xl animate-ping" style={{animationDelay: '1s'}}>🛠️</div>
          </div>
        </div>

        {/* Error Details */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                <span className="text-2xl">🔴</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Sistema Sob Pressão</h3>
              <p className="text-gray-600 text-sm">Nossos servidores estão trabalhando além da capacidade</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                <span className="text-2xl">⚙️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Diagnóstico em Andamento</h3>
              <p className="text-gray-600 text-sm">Nossa equipe está identificando a causa raiz</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Recuperação Programada</h3>
              <p className="text-gray-600 text-sm">Voltaremos ao normal em poucos minutos</p>
            </div>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 mb-8 border border-red-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Status da Recuperação</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Diagnóstico do Problema</span>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse mr-2"></div>
                <span className="text-green-600 font-medium">Concluído</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Aplicação de Correções</span>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-500 rounded-full animate-spin mr-2"></div>
                <span className="text-yellow-600 font-medium">Em Progresso</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Teste de Funcionalidade</span>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-300 rounded-full mr-2"></div>
                <span className="text-gray-500 font-medium">Pendente</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-700">Restauração Completa</span>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-300 rounded-full mr-2"></div>
                <span className="text-gray-500 font-medium">Pendente</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold text-lg px-8 py-4 rounded-lg hover:from-red-600 hover:to-orange-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
          >
            🔄 Tentar Novamente
          </button>
          <Link 
            to="/" 
            className="bg-[#2F5496] text-white font-bold text-lg px-8 py-4 rounded-lg hover:bg-[#1e40af] transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
          >
            🏠 Voltar ao Início
          </Link>
          <Link 
            to="/contact" 
            className="bg-transparent border-2 border-red-500 text-red-500 font-bold text-lg px-8 py-4 rounded-lg hover:bg-red-500 hover:text-white transition-all duration-300 transform hover:scale-105"
          >
            🆘 Reportar Problema
          </Link>
        </div>

        {/* Technical Details */}
        <div className="mt-12 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Detalhes Técnicos</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <p><strong>Código de Erro:</strong> HTTP 500 - Internal Server Error</p>
              <p><strong>Timestamp:</strong> {new Date().toLocaleString('pt-BR')}</p>
            </div>
            <div>
              <p><strong>ID da Sessão:</strong> {Math.random().toString(36).substr(2, 9)}</p>
              <p><strong>Status:</strong> Manutenção em andamento</p>
            </div>
          </div>
        </div>

        {/* Fun Facts */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm mb-4">💡 Curiosidade Técnica:</p>
          <p className="text-gray-600 text-lg italic">
            "Erros 500 são como quebra-cabeças para desenvolvedores - cada um é único e requer uma solução personalizada!"
          </p>
        </div>

        {/* Floating Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 left-10 w-4 h-4 bg-red-500 rounded-full opacity-20 animate-ping"></div>
          <div className="absolute top-40 right-20 w-6 h-6 bg-orange-500 rounded-full opacity-30 animate-bounce" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-40 left-20 w-3 h-3 bg-yellow-500 rounded-full opacity-40 animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-5 h-5 bg-red-400 rounded-full opacity-25 animate-ping" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-orange-400 rounded-full opacity-30 animate-pulse" style={{animationDelay: '0.5s'}}></div>
        </div>
      </div>
    </div>
  )
}

export default ServerError
